import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ProposalService } from '../../../../service/proposal-service/proposal.service';
import { PatientService } from '../../../../service/patient-service/patient.service';
import { decryptField } from '../../../../utils/decrypt-utils';
import * as moment from 'moment-timezone';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { sweetAlert } from '../../../../utils/swal';

@Component({
  selector: 'app-proposal-detail-modal',
  templateUrl: './proposal-detail-modal.component.html',
  styleUrls: ['./proposal-detail-modal.component.css']
})
export class ProposalDetailModalComponent implements OnInit, OnDestroy {
  @Input() proposalId: number | string;
  @Output() onCloseModal = new EventEmitter<void>();

  isRouteMode: boolean = false;
  proposal: any = null;
  isLoading: boolean = true;
  isSubmitting: boolean = false;
  errorMessage: string = '';
  selectedViewFacilityId: number | string | null = null;
  selectedViewFacilityCard: any = null;

  proposalTitle: string = 'Proposal Details';
  proposalSummary: any = {};
  patientInfo: any = {};
  agentInfo: any = {};
  coverLetter: string = '';
  recommendedFacilities: any[] = [];

  levelOfCareMap: { [key: number]: string } = {
    1: 'Assisted Living',
    2: 'Board & Care',
    3: 'Memory Care',
    4: 'Independent Living',
    5: 'Skilled Nursing'
  };

  specialCareMap: { [key: number]: string } = {
    1: 'Oxygen',
    2: 'Catheter',
    3: 'Catheter (Self)',
    4: 'Bedsore',
    5: 'Colostomy',
    6: 'Colostomy (Self)'
  };

  dietRestrictionMap: { [key: number]: string } = {
    1: 'Diabetic',
    2: 'Low Salt',
    3: 'Renal',
    4: 'Puree'
  };

  entitlementMap: { [key: number]: string } = {
    1: 'Medi-Cal',
    2: 'ALWPP',
    3: 'Medi-Cal Pending',
    4: 'VA'
  };

  preIntakeData: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private proposalService: ProposalService,
    private toastr: ToastrService,
    private patientService: PatientService
  ) { }

  ngOnInit(): void {
    if (!this.proposalId) {
      this.route.params.pipe(takeUntil(componentDestroyed(this))).subscribe(params => {
        if (params['id']) {
          this.proposalId = params['id'];
          this.isRouteMode = true;
          this.fetchProposalDetails();
        }
      });
    } else {
      this.fetchProposalDetails();
    }
  }

  ngOnDestroy(): void { }

  closeModal(): void {
    if (this.isRouteMode) {
      this.router.navigate(['/placement/assists']);
    } else {
      this.onCloseModal.emit();
    }
  }

  onViewDetail(facility: any): void {
    if (facility && facility.facility_id) {
      this.selectedViewFacilityId = facility.facility_id;
      this.selectedViewFacilityCard = facility;
    } else if (facility && facility.id) {
      this.selectedViewFacilityId = facility.id;
      this.selectedViewFacilityCard = facility;
    } else {
      this.toastr.error('Facility ID is missing.');
    }
  }

  closeFacilityDetailModal(): void {
    this.selectedViewFacilityId = null;
    this.selectedViewFacilityCard = null;
  }

  onWantFacility(facility: any): void {
    if (!this.proposalId) {
      this.toastr.error('Proposal ID is missing.');
      return;
    }
    if (!facility || !facility.facility_id) {
      this.toastr.error('Facility ID is missing.');
      return;
    }

    Swal.fire({
      title: 'Accept Proposal',
      text: `Are you sure you want to accept ${facility.name}?`,
      input: 'textarea',
      inputPlaceholder: 'Enter notes here...',
      showCancelButton: true,
      confirmButtonColor: '#5163B7',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.value !== undefined) {
        const notes = result.value || '';
        this.submitVerifiedAccept(facility.facility_id, notes);
      }
    });
  }

  submitVerifiedAccept(facilityId: string | number, notes: string): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.proposalService.verifiedAccept(this.proposalId, facilityId, notes)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isSubmitting = false;
          this.toastr.success('Proposal accepted successfully.');
          if (this.proposal) {
            this.proposal.status = 2;
          }
          this.recommendedFacilities.forEach(f => {
            f.isSelected = (f.facility_id === facilityId || f.id === facilityId);
          });
        },
        (error) => {
          this.isSubmitting = false;
          console.error('Error accepting proposal:', error);
          const errorMsg = error && error.error && error.error.message ? error.error.message : 'Failed to accept proposal.';
          this.toastr.error(errorMsg);
        }
      );
  }

  get selectedFacility(): any {
    return (this.recommendedFacilities || []).find(f => f.isSelected);
  }

  onCancelProposal(): void {
    const selected = this.selectedFacility;
    if (!selected) {
      this.toastr.error('No facility selected.');
      return;
    }
    if (!this.proposalId) {
      this.toastr.error('Proposal ID is missing.');
      return;
    }

    sweetAlert('Alert', 'Are you sure you want to cancel?', 'warning', 'Yes', 'No')
      .then((result: any) => {
        if (result && result.value) {
          this.submitDecline(selected.facility_id, 'Test notes');
        }
      });
  }

  submitDecline(facilityId: string | number, notes: string): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.proposalService.decline(this.proposalId, facilityId, notes)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isSubmitting = false;
          this.toastr.success('Proposal cancelled successfully.');
          this.closeModal();
        },
        (error) => {
          this.isSubmitting = false;
          console.error('Error cancelling proposal:', error);
          const errorMsg = error && error.error && error.error.message ? error.error.message : 'Failed to cancel proposal.';
          this.toastr.error(errorMsg);
        }
      );
  }

  fetchProposalDetails(): void {
    if (!this.proposalId) {
      this.isLoading = false;
      this.errorMessage = 'Proposal ID is missing.';
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    forkJoin([
      this.patientService.getPreIntakeData(),
      this.proposalService.getProposalDetails(this.proposalId)
    ]).pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        ([preIntake, res]) => {
          this.isLoading = false;
          this.preIntakeData = preIntake;
          const data = (res && res.data) ? res.data : (res && res.proposal ? res.proposal : res);
          this.proposal = data;
          this.processProposalData(data);
        },
        (error) => {
          console.error('Error fetching proposal details:', error);
          this.isLoading = false;
          this.errorMessage = 'Failed to load proposal details.';
        }
      );
  }

  processProposalData(data: any): void {
    if (!data) return;

    this.proposalTitle = data.title || 'Proposal Details';

    // 1. Patient Info / Broadcast Details (Source: data.e_patientbroadcastdetails)
    const broadcastDetails = data.e_patientbroadcastdetails || data.patientbroadcastdetails || {};
    
    // Summary Fields
    const statusNum = typeof data.status === 'string' ? parseInt(data.status, 10) : Number(data.status);
    let statusLabel = 'N/A';
    let statusColor = '#6b7280';
    switch (statusNum) {
      case 0:
        statusLabel = 'Draft';
        statusColor = '#16a7d1';
        break;
      case 1:
        statusLabel = 'Sent';
        statusColor = '#2dce89';
        break;
      case 2:
        statusLabel = 'Accepted';
        statusColor = '#4d94dd';
        break;
    }

    let createdDateStr = 'N/A';
    if (data.created_date) {
      createdDateStr = moment(data.created_date).isValid()
        ? moment(data.created_date).tz('America/Los_Angeles').format('MMMM DD, YYYY hh:mm:ss A')
        : data.created_date;
    }

    let budgetStr = 'N/A';
    if (broadcastDetails.budget_min !== undefined && broadcastDetails.budget_max !== undefined && broadcastDetails.budget_min !== null && broadcastDetails.budget_max !== null) {
      const minVal = Number(broadcastDetails.budget_min);
      const maxVal = Number(broadcastDetails.budget_max);
      const minStr = isNaN(minVal) ? broadcastDetails.budget_min : minVal.toFixed(2);
      const maxStr = isNaN(maxVal) ? broadcastDetails.budget_max : maxVal.toFixed(2);
      budgetStr = `${broadcastDetails.budgettype_id || 'Monthly'} ($${minStr} - $${maxStr})`;
    }

    let preferredLocStr = 'N/A';
    if (broadcastDetails.preferred_location && Array.isArray(broadcastDetails.preferred_location) && broadcastDetails.preferred_location.length) {
      const loc = broadcastDetails.preferred_location[0];
      preferredLocStr = [loc.city_name, loc.state_name].filter(Boolean).join(', ') || 'N/A';
    } else {
      const cityName = (broadcastDetails.e_cities && broadcastDetails.e_cities.name) || broadcastDetails.city_name || '';
      const stateName = (broadcastDetails.e_states && broadcastDetails.e_states.name) || broadcastDetails.state_name || '';
      preferredLocStr = [cityName, stateName].filter(Boolean).join(', ') || 'N/A';
    }

    const accType = broadcastDetails.e_accommodation_type || broadcastDetails.e_accommodations || {};
    const accommodationName = accType.name || broadcastDetails.accommodation_type || 'N/A';

    let lengthOfStayStr = 'N/A';
    if (broadcastDetails.onboarding_date && broadcastDetails.departure_date && moment(broadcastDetails.onboarding_date).isValid() && moment(broadcastDetails.departure_date).isValid()) {
      const nights = moment(broadcastDetails.departure_date).startOf('day').diff(moment(broadcastDetails.onboarding_date).startOf('day'), 'days') + 1;
      if (nights >= 0) {
        lengthOfStayStr = `${nights} nights`;
      }
    }

    let walkthroughDateStr = 'N/A';
    if (broadcastDetails.walkthrough_date) {
      walkthroughDateStr = moment(broadcastDetails.walkthrough_date).isValid()
        ? moment(broadcastDetails.walkthrough_date).tz('America/Los_Angeles').format('MMMM DD, YYYY hh:mm:ss A')
        : broadcastDetails.walkthrough_date;
    }

    this.proposalSummary = {
      referenceNo: data.reference_id || 'N/A',
      preferredLocation: preferredLocStr,
      budget: budgetStr,
      statusLabel: statusLabel,
      statusColor: statusColor,
      createdDate: createdDateStr,
      onboardingDate: broadcastDetails.onboarding_date || 'N/A',
      departureDate: broadcastDetails.departure_date || 'N/A',
      walkthroughDate: walkthroughDateStr,
      accommodationName: accommodationName,
      lengthOfStay: lengthOfStayStr,
      coverLetter: (data.cover_letter || '').trim() || 'N/A'
    };

    // Patient Fields
    const intake = broadcastDetails.e_intakes ? (Array.isArray(broadcastDetails.e_intakes) ? broadcastDetails.e_intakes[0] : broadcastDetails.e_intakes) : (broadcastDetails.e_intake || broadcastDetails.intake || {});
    const patient = broadcastDetails.e_patients
      ? (Array.isArray(broadcastDetails.e_patients) ? broadcastDetails.e_patients[0] : broadcastDetails.e_patients)
      : ((intake && intake.e_patients) ? (Array.isArray(intake.e_patients) ? intake.e_patients[0] : intake.e_patients) : (broadcastDetails.patient || intake.patient || {}));

    const firstName = decryptField(patient.first_name || intake.first_name);
    const lastName = decryptField(patient.last_name || intake.last_name);
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    const phone = decryptField(patient.phone || intake.phone);
    const rawDob = patient.date_of_birth || patient.dob || intake.date_of_birth;
    const decryptedDob = rawDob ? decryptField(rawDob) : null;
    const formattedDob = decryptedDob && moment(decryptedDob).isValid() ? moment(decryptedDob).format('MM/DD/YYYY') : (decryptedDob || '—');
    const age = decryptedDob && moment(decryptedDob).isValid() ? moment().diff(moment(decryptedDob), 'years') : null;

    const rawIncomeVal = intake.annual_income || patient.annual_income;
    const rawIncome = rawIncomeVal ? decryptField(rawIncomeVal) : null;
    const formattedIncome = rawIncome ? (isNaN(Number(rawIncome)) ? rawIncome : `$${Number(rawIncome).toLocaleString()}`) : '—';

    const specialCareNeedsStr = this.mapListItems(intake.e_patientintakespecialcare || broadcastDetails.e_patientintakespecialcare, 'intakespecialcareneeds_id', this.specialCareMap);
    const dietRestrictionsStr = this.mapListItems(intake.e_patientintakedietrestriction || broadcastDetails.e_patientintakedietrestriction, 'intakedietrestrictions_id', this.dietRestrictionMap);
    const entitlementsStr = this.mapListItems(intake.e_patientintakeentitlement || broadcastDetails.e_patientintakeentitlement, 'intakeentitlements_id', this.entitlementMap);

    const levelOfCare = (intake.e_levelofcare && intake.e_levelofcare.name)
      || (broadcastDetails.e_levelofcare && broadcastDetails.e_levelofcare.name)
      || this.levelOfCareMap[intake.levelofcare_id]
      || this.levelOfCareMap[broadcastDetails.levelofcare_id]
      || '—';

    const rawDiagnosis = decryptField(intake.diagnosis || patient.diagnosis);
    const diagnosisStr = rawDiagnosis || (intake.e_primarydiagnosis && intake.e_primarydiagnosis.name) || '—';

    const medicationNotes = decryptField(intake.medication_notes || patient.medication_notes) || '—';
    const additionalComments = decryptField(intake.additional_comments || patient.additional_comments) || '—';

    let heightStr = '—';
    if (intake.height_feet !== undefined || intake.height_inches !== undefined) {
      if (intake.height_feet > 0 || intake.height_inches > 0) {
        heightStr = `${intake.height_feet || 0}' ${intake.height_inches || 0}"`;
      }
    }

    let weightStr = '—';
    if (intake.weight && Number(intake.weight) > 0) {
      weightStr = `${Number(intake.weight)} lbs`;
    }

    this.patientInfo = {
      name: fullName || '—',
      phone: phone || '—',
      email: patient.email || intake.email || '—',
      levelOfCare: levelOfCare,
      dateOfBirth: formattedDob,
      age: age !== null ? `${age} years` : '—',
      annualIncome: formattedIncome,
      height: heightStr,
      weight: weightStr,
      ambulation: this.getLookupName('ambulation', intake.ambulation),
      transfers: this.getLookupName('transfers', intake.transfers),
      bathing: this.getLookupName('bathing', intake.bathing),
      eating: this.getLookupName('eating', intake.eating),
      mentalStatus: this.getLookupName('mentalStatus', intake.mental_status),
      incontinenceBowel: this.getLookupName('incontinencecare', intake.incontinence_care_bowel || intake.incontinence_bowel),
      incontinenceBladder: this.getLookupName('incontinencecare', intake.incontinence_care_bladder || intake.incontinence_bladder),
      diabetesMedication: this.getLookupName('diabetes', intake.diabetes || intake.diabetes_medication),
      dressing: this.getLookupName('dressing', intake.dressing),
      hospiceImminent: this.capitalize(intake.hospice_imminent),
      specialCareNeeds: specialCareNeedsStr,
      dietRestrictions: dietRestrictionsStr,
      possibleEntitlements: entitlementsStr,
      primaryDiagnosis: diagnosisStr,
      medicationNotes: medicationNotes || '—',
      additionalComments: additionalComments || '—'
    };

    // 2. Agent Info (Source: data.e_placementagents)
    const agent = data.e_placementagents || data.placementagent || data.e_placementagent || data.agent || {};
    const agentFirstName = agent.first_name || '';
    const agentLastName = agent.last_name || '';
    const agentFullName = [agentFirstName, agentLastName].filter(Boolean).join(' ') || agent.name || '—';
    const agentComp = agent.company || agent.company_name || agent.companyName || '—';

    let commission = '—';
    if (agent.commission_rate !== undefined && agent.commission_rate !== null) {
      const rateNum = Number(agent.commission_rate);
      if (!isNaN(rateNum)) {
        if (rateNum > 0 && rateNum <= 1) {
          commission = `${Math.round(rateNum * 100)}%`;
        } else {
          commission = `${rateNum}%`;
        }
      } else {
        commission = agent.commission_rate;
      }
    }

    this.agentInfo = {
      name: agentFullName,
      companyName: agentComp,
      email: agent.email || '—',
      phone: agent.phone || agent.contact_no || '—',
      mailingAddress: agent.mailing_address || agent.address || '—',
      commissionRate: commission,
      totalBroadcastsSent: agent.intakebroadcast_totalsent !== undefined && agent.intakebroadcast_totalsent !== null ? String(agent.intakebroadcast_totalsent) : '—',
      image: agent.profile_image || agent.image || agent.photo || null
    };

    // 3. Cover Letter (Source: data.cover_letter)
    this.coverLetter = data.cover_letter || '—';

    // 4. Recommended Facilities (Source: data.e_agentproposalfacility)
    this.recommendedFacilities = (data.e_agentproposalfacility || []).map((item: any) => {
      const fac = item.e_facilities || {};
      const img = fac.e_facility_media && fac.e_facility_media[0] ? fac.e_facility_media[0].image : null;
      return {
        id: fac.id || item.facility_id,
        facility_id: item.facility_id || fac.id,
        facilitytype_id: fac.facilitytype_id || fac.facility_type_id || (fac.e_facility_types && fac.e_facility_types.id) || (fac.e_facility_type && fac.e_facility_type.id) || null,
        name: fac.name || '—',
        address: fac.street_address || '—',
        cityName: fac.e_cities ? fac.e_cities.name : '',
        stateName: fac.e_states ? fac.e_states.name : '',
        image: img,
        personCount: fac.capacity || 0,
        bedCount: fac.bedrooms || 0,
        bathCount: fac.bathrooms || 0,
        squareFootage: fac.area || 0,
        isSelected: item.status !== undefined && item.status !== null ? Number(item.status) === 1 : false
      };
    });
  }

  private mapListItems(itemsArr: any[], idKey: string, mapObj: { [key: number]: string }): string {
    if (!itemsArr || !Array.isArray(itemsArr) || !itemsArr.length) return '—';
    const mapped = itemsArr.map(item => {
      if (!item) return '';
      return mapObj[item[idKey]] || item.name || item.label || '';
    }).filter(Boolean);
    return mapped.length ? mapped.join(', ') : '—';
  }

  private getLookupName(listKey: string, val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    if (this.preIntakeData && this.preIntakeData[listKey] && Array.isArray(this.preIntakeData[listKey])) {
      const option = this.preIntakeData[listKey].find(opt => Number(opt.id) === Number(val));
      if (option && option.name) return option.name;
    }
    return this.capitalize(val);
  }

  private capitalize(val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    if (typeof val === 'number') return String(val);
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      const label = val.name || val.label || val.title || val.value;
      if (!label) return '—';
      const strLabel = String(label);
      return strLabel.charAt(0).toUpperCase() + strLabel.slice(1).toLowerCase();
    }
    const str = String(val);
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
}
